import { useContext } from "react";
import { Box, Grid, MenuItem, Select, TextField } from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";

import { AuthContext } from "../../../contexts/AuthContext";
import { CONFIG } from "../../../services/config";

import { FormInputDate } from "../../../components/form/FormInputDate";
import { FormInputText } from "../../../components/form/FormInputText";
import ActionButtons from "../../../components/MyModal/ActionButtons";
import MyModal from "../../../components/MyModal";

import { COMPLETED, CANCELLED } from "../../../constants";
import formatStatus from "../../../utils/formatStatusName";

type Props = {
  onClose: () => void;
  openSucessAlert: () => void;
  openErrorAlert: () => void;
  refreshPage: () => void;
  setActionType: (type: string) => void;
};

export interface FormValues {
  vin: string;
  serviceLost: string;
  odometer: number;
  osNumber: number;
  revisionDate: string;
  status: string;
  dn: string;
}

export default function ModalAddRevalidation({
  onClose,
  openSucessAlert,
  openErrorAlert,
  refreshPage,
  setActionType,
}: Props) {
  const { t } = useTranslation();
  const { tokenHeaders } = useContext(AuthContext);
  const status = [COMPLETED, CANCELLED];

  const formData = yup.object().shape({
    vin: yup.string().label(t("revalidation.add.chassi")),
    odometer: yup.string().required(t("revalidation.add.odometerError")),
    osNumber: yup.string().required(t("revalidation.add.osNumberError")),
    revisionDate: yup.string().required(t("revalidation.add.revisionDate")),
    serviceLost: yup.string().label(t("revalidation.add.lostService")),
    status: yup.string().label(t("revalidation.add.status")),
    dn: yup.string().required(t("revalidation.add.dnCode")),
  });

  const { register, control, handleSubmit, formState } = useForm<FormValues>({
    resolver: yupResolver(formData) as any,
  });

  const { errors } = formState;

  const HorizontalLine = (
    <Box
      sx={{
        borderTop: "1px solid var(--gray-100)",
        marginBottom: 3,
        marginTop: 2,
      }}
    />
  );

  const addRevalidationService: SubmitHandler<FormValues> = async (values) => {
    try {
      await axios.post(
        `${CONFIG.SOB_API}revalidate-plan`,
        {
          vin: values.vin,
          odometer: values.odometer,
          osNumber: values.osNumber,
          revisionDate: values.revisionDate,
          serviceLost: values.serviceLost,
          status: values.status,
          dn: values.dn,
        },
        tokenHeaders,
      );
      onClose();
      refreshPage();
      openSucessAlert();
      setActionType("add");
    } catch (error: any) {
      onClose();
      openErrorAlert();
    }
  };

  return (
    <MyModal
      title={t("revalidation.add.titleModal")}
      modalChange={onClose}
      component="form"
      onSubmit={handleSubmit(addRevalidationService)}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }}>
          <span>{t("revalidation.add.vehicle")}</span>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <FormInputText
            id="vin"
            label={t("revalidation.add.chassi")}
            {...register("vin")}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormInputText
            id="odometer"
            type="number"
            label={t("revalidation.add.odometer")}
            inputProps={{ max: 999999 }}
            error={errors.odometer}
            {...register("odometer")}
          />
        </Grid>
      </Grid>

      {HorizontalLine}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }}>
          <span>{t("revalidation.add.service")}</span>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <FormInputText
            id="osNumber"
            type="text"
            label={t("revalidation.add.osNumber")}
            inputProps={{
              maxLength: 10,
              minLength: 1,
            }}
            error={errors.osNumber}
            {...register("osNumber")}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormInputDate
            name="revisionDate"
            control={control}
            label={t("revalidation.add.revisionDate")}
            minDate={dayjs("1900-01-01")}
            maxDate={dayjs("2100-01-01")}
            error={errors.revisionDate}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }} />
        <Grid size={{ xs: 12, md: 5 }}>
          <FormInputText
            id="serviceLost"
            label={t("revalidation.add.lostService")}
            {...register("serviceLost")}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }} style={{ height: "10px" }}>
          <FormInputText
            select
            id="status"
            label={t("revalidation.add.status")}
            {...register("status")}
          >
            {status.map((status) => (
              <MenuItem key={status} value={status}>
                {formatStatus(status)}
              </MenuItem>
            ))}
          </FormInputText>
        </Grid>
      </Grid>

      {HorizontalLine}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }}>
          <span>{t("revalidation.add.dealer")}</span>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <FormInputText
            id="dn"
            type="number"
            label={t("revalidation.add.dnCode")}
            inputProps={{ max: 99999999999 }}
            error={errors.dn}
            {...register("dn")}
          />
        </Grid>
      </Grid>

      {HorizontalLine}

      <p style={{ fontSize: 12, marginBottom: 30 }}>
        {t("revalidation.add.requiredFields")}
      </p>

      <ActionButtons
        typeButton="submit"
        saveName={t("button.Save")}
        enableLine={false}
      />
    </MyModal>
  );
}
